import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type {
  CustomFieldRawValue,
  CustomFieldValueViewModel,
  CustomFieldViewModel,
} from '../../custom-fields/models/custom-field.model';
import {
  formatNumberFieldValueForApi,
  parseFlexibleNumberString,
  resolveNumberFieldDecimalPlaces,
  shouldPreserveLeadingZeroIntegerRaw,
  roundToDecimalPlaces,
} from '../../custom-fields/utils/number-field.util';
import { evaluateFormulaNumericPreview } from '../../custom-fields/models/formula-field.model';
import { CustomFieldsDataService } from '../../custom-fields/services/custom-fields-data.service';
import type {
  CardFormState,
  CreateCardInput,
  CreateCardResult,
  UpdateCardInput,
} from '../models/card-form.model';
import type {
  CardAttachmentUploadResultModel,
  CardFieldValueModel,
} from '../models/card-details-api.models';
import { CardFieldValuesApiService } from '../api/card-field-values-api.service';
import { CardAttachmentsApiService } from '../api/card-attachments-api.service';
import { CardChecklistsApiService } from '../api/card-checklists-api.service';
import { CardChecklistItemsApiService } from '../api/card-checklist-items-api.service';
import { WorkflowItemsService } from '../../workflows/services/workflow-items.service';
import { CompanyUsersService } from '../../../core/services/company-users.service';
import { isGuidString } from '../../../core/utils/guid.util';
import { normalizeBrazilPhoneForSubmit } from '../../../shared/utils/brazil-phone.util';
import {
  parseFileFieldValueToAttachments,
  type FileFieldAttachment,
} from '../../../shared/utils';

@Injectable({ providedIn: 'root' })
export class CardsDataService {
  private readonly workflowItemsService = inject(WorkflowItemsService);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly customFieldsData = inject(CustomFieldsDataService);
  private readonly cardFieldValuesApi = inject(CardFieldValuesApiService);
  private readonly cardAttachmentsApi = inject(CardAttachmentsApiService);
  private readonly cardChecklistsApi = inject(CardChecklistsApiService);
  private readonly cardChecklistItemsApi = inject(CardChecklistItemsApiService);

  async createCard(input: CreateCardInput): Promise<CreateCardResult> {
    const ownerId = input.state.assigneeUserId?.trim() ?? '';
    const assigneeName = ownerId ? this.companyUsers.resolveUserDisplayName(ownerId) : '';
    const valuesWithFormulas = this.computeFormulaValues(input.workflowId, input.state.customFieldValues);
    const { batch, postSync } = await this.buildCreateFieldValuesBatch(
      input.workflowId,
      valuesWithFormulas
    );

    let createdId: string;
    let batchApplied = false;

    try {
      const created = await this.workflowItemsService.addItem(input.workflowId, input.projectId, {
        title: input.state.title,
        description: input.state.description || undefined,
        status: input.state.stageId,
        priority: 'Média',
        dueDate: input.state.dueDate,
        assigneeId: ownerId || undefined,
        assigneeName: assigneeName || undefined,
        customFields: this.toCustomFieldsMap(valuesWithFormulas),
        createFieldValues: batch.length > 0 ? batch : undefined,
      });
      createdId = created.id;
      batchApplied = batch.length > 0;
    } catch (err) {
      if (batch.length > 0 && err instanceof HttpErrorResponse && err.status === 400) {
        const recoveredId = await this.tryRecoverCreatedCardId(input.workflowId);
        if (recoveredId) {
          createdId = recoveredId;
        } else {
          throw err;
        }
      } else {
        throw err;
      }
    }

    /**
     * O card já está no `itemsSignal` após `addItem` (optimistic update). Os passos abaixo
     * — sync de valores + refresh — rodam em background para o modal fechar na hora.
     */
    const fieldValuesSynced = this.finalizeCreatedCard(
      createdId,
      input.workflowId,
      valuesWithFormulas,
      postSync,
      batchApplied,
      input.state.checklistItems ?? []
    );

    return { cardId: createdId, fieldValuesSynced };
  }

  private async finalizeCreatedCard(
    cardId: string,
    workflowId: string,
    valuesWithFormulas: CustomFieldValueViewModel[],
    postSync: CustomFieldValueViewModel[],
    batchApplied: boolean,
    checklistItems: string[] = []
  ): Promise<boolean> {
    let fieldValuesSynced = false;
    try {
      if (batchApplied && postSync.length === 0) {
        fieldValuesSynced = true;
      } else if (batchApplied && postSync.length > 0) {
        /**
         * `partial: true` — só sincroniza FILE (postSync). Sem isso, o loop de orphans
         * apagava todos os campos não-FILE que o batch acabou de gravar.
         */
        await this.syncCardFieldValues(cardId, workflowId, postSync, {
          assumeEmptyRemote: false,
          partial: true,
        });
        fieldValuesSynced = true;
      } else {
        await this.syncCardFieldValues(cardId, workflowId, valuesWithFormulas, {
          assumeEmptyRemote: true,
        });
        fieldValuesSynced = true;
      }
    } catch {
      // Card já foi criado na API; falha ao gravar valores não deve rejeitar a Promise.
    }

    try {
      await this.refreshCardValuesCache(cardId, valuesWithFormulas);
    } catch {
      /* snapshot local best-effort */
    }
    try {
      await this.workflowItemsService.refreshCardFromApi(cardId);
    } catch {
      /* falha de rede: mantém quickFields montados por fieldId no addItem */
    }

    if (checklistItems.length > 0) {
      try {
        await this.createChecklistWithItems(cardId, checklistItems);
      } catch {
        /* checklist é best-effort — card já foi criado */
      }
    }

    return fieldValuesSynced;
  }

  private async createChecklistWithItems(cardId: string, items: string[]): Promise<void> {
    const checklist = await this.cardChecklistsApi.create(cardId, {
      cardId,
      title: 'Checklist',
      order: 1,
    });
    if (!checklist?.id) return;
    for (let i = 0; i < items.length; i++) {
      const title = items[i].trim();
      if (!title) continue;
      await this.cardChecklistItemsApi.create(cardId, checklist.id, {
        cardId,
        cardChecklistId: checklist.id,
        title,
        order: i + 1,
      });
    }
  }

  async updateCard(input: UpdateCardInput): Promise<boolean> {
    const updated = await this.workflowItemsService.updateItem(input.cardId, {
      title: input.state.title,
      description: input.state.description || undefined,
      status: input.state.stageId,
      dueDate: input.state.dueDate,
    });

    if (!updated) return false;
    const valuesWithFormulas = this.computeFormulaValues(input.workflowId, input.state.customFieldValues);
    await this.syncCardFieldValues(input.cardId, input.workflowId, valuesWithFormulas, {
      assumeEmptyRemote: false,
    });
    await this.refreshCardValuesCache(input.cardId, valuesWithFormulas);
    try {
      await this.workflowItemsService.refreshCardFromApi(input.cardId);
    } catch {
      /* best effort para manter board/lista alinhados */
    }
    return true;
  }

  /**
   * Grava na API os valores de campos customizados a partir do mapa da lista (edição inline).
   * Mantém `CustomFieldsDataService` alinhado ao detalhe do card.
   */
  async syncInlineCustomFieldMap(
    cardId: string,
    workflowId: string,
    fields: Record<string, string | number | boolean | string[] | null>
  ): Promise<void> {
    const values: CustomFieldValueViewModel[] = Object.entries(fields).map(([fieldId, value]) => ({
      fieldId,
      value: value as CustomFieldRawValue,
    }));
    await this.syncCardFieldValues(cardId, workflowId, values);
    this.customFieldsData.setValuesByCard(cardId, values);
  }

  getCardFormState(cardId: string, workflowId: string): CardFormState | null {
    const item = this.workflowItemsService.getById(cardId);
    if (!item || item.workflowId !== workflowId) return null;

    const values = this.customFieldsData.getValuesByCard(cardId);
    return {
      title: item.title,
      description: item.description ?? '',
      assigneeUserId: null,
      dueDate: item.dueDate ?? null,
      stageId: item.status,
      customFieldValues: values,
      checklistItems: [],
    };
  }

  /**
   * Calcula valores de campos FORMULA a partir dos operandos preenchidos no formulário
   * e injeta no array de valores. Garante que fórmulas sejam persistidas junto com o card.
   */
  private computeFormulaValues(
    workflowId: string,
    values: CustomFieldValueViewModel[]
  ): CustomFieldValueViewModel[] {
    const fields = this.customFieldsData.getFieldsByWorkflow(workflowId);
    const formulaFields = fields.filter(f => f.type === 'FORMULA');
    if (!formulaFields.length) return values;

    const result = [...values];
    for (const formula of formulaFields) {
      const computed = evaluateFormulaNumericPreview(
        formula.formulaDefinition,
        (fieldId) => {
          const entry = result.find(v => v.fieldId === fieldId);
          return entry?.value ?? null;
        }
      );
      if (computed == null) continue;

      const dp = formula.formulaResultDecimalPlaces ?? 0;
      const rounded = roundToDecimalPlaces(computed, dp);

      const idx = result.findIndex(v => v.fieldId === formula.fieldId);
      if (idx >= 0) {
        result[idx] = { fieldId: formula.fieldId, value: rounded };
      } else {
        result.push({ fieldId: formula.fieldId, value: rounded });
      }
    }
    return result;
  }

  /**
   * Quando POST /api/cards retorna 400 mas o card foi persistido (ex.: falha em fieldValues/automação),
   * recarrega os cards do workflow e detecta o card recém-criado comparando IDs.
   */
  private async tryRecoverCreatedCardId(workflowId: string): Promise<string | null> {
    const existingIds = new Set(
      this.workflowItemsService.getByWorkflow(workflowId).map(c => c.id)
    );
    try {
      await this.workflowItemsService.reloadWorkflowCards(workflowId);
    } catch {
      return null;
    }
    const newCard = this.workflowItemsService
      .getByWorkflow(workflowId)
      .find(c => !existingIds.has(c.id));
    return newCard?.id ?? null;
  }

  private toCustomFieldsMap(
    values: CustomFieldValueViewModel[]
  ): Record<string, string | number | boolean | string[] | null> {
    return values.reduce<Record<string, string | number | boolean | string[] | null>>((acc, item) => {
      const v = item.value;
      if (v instanceof File) acc[item.fieldId] = null;
      else if (Array.isArray(v) && v.length > 0 && v.every((x) => x instanceof File)) acc[item.fieldId] = null;
      else if (Array.isArray(v) && v.some((x) => x instanceof File)) acc[item.fieldId] = null;
      else acc[item.fieldId] = v as string | number | boolean | string[] | null;
      return acc;
    }, {});
  }

  private async syncCardFieldValues(
    cardId: string,
    workflowId: string,
    values: CustomFieldValueViewModel[],
    options?: { assumeEmptyRemote?: boolean; partial?: boolean }
  ): Promise<void> {
    const fieldsById = new Map(
      this.customFieldsData
        .getFieldsByWorkflow(workflowId)
        .map((field) => [this.normalizeFieldId(field.fieldId), field] as const)
    );
    const remote = options?.assumeEmptyRemote
      ? { items: [] as CardFieldValueModel[] }
      : await this.cardFieldValuesApi.getByCard(cardId);
    const remoteByFieldId = new Map(
      remote.items.map((entry) => [this.normalizeFieldId(entry.workflowFieldId), entry] as const)
    );
    const incomingByFieldId = new Map<string, CustomFieldValueViewModel>();
    for (const entry of values) {
      const resolved = this.resolveWorkflowFieldIdForApi(workflowId, entry.fieldId ?? '');
      if (!resolved) continue;
      incomingByFieldId.set(this.normalizeFieldId(resolved), entry);
    }

    for (const entry of values) {
      const workflowFieldId = this.resolveWorkflowFieldIdForApi(workflowId, entry.fieldId ?? '');
      if (!workflowFieldId) continue;

      const field = fieldsById.get(this.normalizeFieldId(workflowFieldId));
      const fieldType = field?.type;

      if (fieldType === 'FORMULA') {
        continue;
      }

      if (fieldType === 'FILE') {
        const v = entry.value;
        if (v instanceof File) {
          await this.cardAttachmentsApi.upload(cardId, v);
        } else if (Array.isArray(v) && v.length > 0 && v.every((x) => x instanceof File)) {
          for (const f of v as File[]) {
            await this.cardAttachmentsApi.upload(cardId, f);
          }
        }
        continue;
      }

      const existingRemoteValue = remoteByFieldId.get(this.normalizeFieldId(workflowFieldId))?.value;
      const hasRemote = remoteByFieldId.has(this.normalizeFieldId(workflowFieldId));
      const serialized = await this.toPersistedValue(cardId, entry.value, field, existingRemoteValue);

      if (serialized === null) {
        if (hasRemote) {
          await this.cardFieldValuesApi.delete(cardId, workflowFieldId);
        }
        continue;
      }

      await this.upsertFieldValue(cardId, workflowFieldId, serialized, hasRemote);
    }

    /**
     * Em modo `partial`, `values` representa só um subconjunto a atualizar (ex.: pós-criação de card,
     * onde os outros campos já foram persistidos via batch no POST /api/cards). NÃO devemos apagar
     * o que está remoto e não veio em `values` — isso destruiria os campos recém-gravados pelo batch.
     */
    if (options?.partial) return;

    for (const existing of remote.items) {
      const normalizedFieldId = this.normalizeFieldId(existing.workflowFieldId);
      if (!normalizedFieldId || incomingByFieldId.has(normalizedFieldId)) continue;
      await this.cardFieldValuesApi.delete(cardId, existing.workflowFieldId);
    }
  }

  /**
   * A lista grava chaves normalizadas (slug do nome); a API exige `workflowFieldId` GUID.
   */
  private resolveWorkflowFieldIdForApi(workflowId: string, rawKey: string): string | null {
    const t = (rawKey ?? '').trim();
    if (!t) return null;
    if (isGuidString(t)) return t;
    const norm = this.normalizeCustomFieldSlug(t);
    for (const f of this.customFieldsData.getFieldsByWorkflow(workflowId)) {
      const slug = this.normalizeCustomFieldSlug(f.key || f.name || '');
      if (slug.length > 0 && slug === norm) return f.fieldId;
    }
    return null;
  }

  private normalizeCustomFieldSlug(value: string): string {
    return (value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private async upsertFieldValue(
    cardId: string,
    workflowFieldId: string,
    value: string | number | boolean,
    hasRemote: boolean
  ): Promise<void> {
    const payload = { cardId, workflowFieldId, value };
    if (hasRemote) {
      try {
        await this.cardFieldValuesApi.update(payload);
        return;
      } catch (error) {
        if (!this.isRecoverableUpsertError(error)) throw error;
      }
      await this.cardFieldValuesApi.create(payload);
      return;
    }

    try {
      await this.cardFieldValuesApi.create(payload);
      return;
    } catch (error) {
      if (!this.isRecoverableUpsertError(error)) throw error;
    }
    await this.cardFieldValuesApi.update(payload);
  }

  private async toPersistedValue(
    cardId: string,
    value: CustomFieldRawValue,
    field?: CustomFieldViewModel,
    existingRemoteValue?: unknown
  ): Promise<string | number | boolean | null> {
    if (value == null) return null;

    const fieldType = field?.type;

    if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX') {
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return 'true';
        if (normalized === 'false' || normalized === '0') return 'false';
      }
      return null;
    }

    if (fieldType === 'FILE') {
      const existingJson = this.normalizeExistingFileFieldJson(existingRemoteValue);
      const existingAttachments = parseFileFieldValueToAttachments(existingJson ?? '');
      if (value instanceof File) {
        const uploaded = await this.cardAttachmentsApi.upload(cardId, value);
        return this.serializeFileAttachmentsForApi([
          ...existingAttachments,
          this.descriptorFromUpload(uploaded),
        ]);
      }
      if (Array.isArray(value) && value.length > 0 && value.every((x) => x instanceof File)) {
        const pieces: FileFieldAttachment[] = [];
        for (const f of value as File[]) {
          const uploaded = await this.cardAttachmentsApi.upload(cardId, f);
          pieces.push(this.descriptorFromUpload(uploaded));
        }
        return this.serializeFileAttachmentsForApi([...existingAttachments, ...pieces]);
      }
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (!this.isValidFileFieldStringValue(trimmed)) return null;
      return trimmed;
    }

    if (fieldType === 'PHONE' && typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      const normalized = normalizeBrazilPhoneForSubmit(trimmed);
      if (/^\+[1-9]\d{6,14}$/.test(normalized)) return normalized;
      return trimmed.length ? trimmed : null;
    }

    if (fieldType === 'PERSON' || fieldType === 'USER') {
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      if (isGuidString(trimmed)) return trimmed;
      const resolvedId = this.companyUsers.resolveUserIdByDisplayName(trimmed);
      return resolvedId && isGuidString(resolvedId) ? resolvedId : null;
    }

    if (fieldType === 'NUMBER' || fieldType === 'PROGRESS') {
      const dp = resolveNumberFieldDecimalPlaces(field, value);
      if (typeof value === 'number' && Number.isFinite(value)) {
        return formatNumberFieldValueForApi(value, dp);
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed.length) return null;
        if (shouldPreserveLeadingZeroIntegerRaw(trimmed, dp)) {
          return trimmed.replace(/\s+/g, '');
        }
        const n = parseFlexibleNumberString(trimmed);
        if (n == null) return null;
        return formatNumberFieldValueForApi(n, dp);
      }
      return null;
    }

    if (typeof value === 'boolean' || typeof value === 'number') return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }
    if (Array.isArray(value)) {
      const serialized = value.map((entry) => String(entry).trim()).filter(Boolean).join(',');
      return serialized.length ? serialized : null;
    }
    return null;
  }

  private descriptorFromUpload(uploaded: CardAttachmentUploadResultModel): FileFieldAttachment {
    const storageKey = (uploaded.storageKey ?? '').trim();
    const urlCandidates = [uploaded.url, uploaded.assetUrl, uploaded.fileUrl, uploaded.path]
      .map((entry) => (entry ?? '').trim())
      .filter((entry) => entry.length > 0);
    const url = urlCandidates.find((entry) => this.isValidFileUrlOrPath(entry)) ?? '';
    const id = (uploaded.id ?? '').trim();
    const name = (uploaded.originalFileName || uploaded.fileName || '').trim() || undefined;
    const out: FileFieldAttachment = {};
    if (id) out.id = id;
    if (name) {
      out.fileName = name;
      out.originalFileName = name;
    }
    if (storageKey) out.storageKey = storageKey;
    if (url) {
      out.url = url;
      out.assetUrl = url;
    }
    return out;
  }

  private isValidFileFieldStringValue(value: string): boolean {
    if (this.isValidFileUrlOrPath(value)) return true;
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      return parseFileFieldValueToAttachments(trimmed).length > 0;
    }
    return false;
  }

  private isValidFileUrlOrPath(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (normalized.startsWith('blob:') || normalized.startsWith('data:')) return false;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) return true;
    return normalized.startsWith('/storage/');
  }

  private isRecoverableUpsertError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    return error.status === 400 || error.status === 404 || error.status === 409;
  }

  /**
   * Monta `fieldValues` para POST /api/cards (batch). Ficheiros e fórmulas ficam para `syncCardFieldValues` após criar.
   */
  private async buildCreateFieldValuesBatch(
    workflowId: string,
    values: CustomFieldValueViewModel[]
  ): Promise<{
    batch: Array<{ workflowFieldId: string; value: string | null }>;
    postSync: CustomFieldValueViewModel[];
  }> {
    if (!values.length) {
      return { batch: [], postSync: [] };
    }
    await this.customFieldsData.ensureWorkflowFieldsLoaded(workflowId);
    const fieldsById = new Map(
      this.customFieldsData
        .getFieldsByWorkflow(workflowId)
        .map((field) => [this.normalizeFieldId(field.fieldId), field] as const)
    );
    const byNorm = new Map<string, { workflowFieldId: string; value: string | null }>();
    const postSync: CustomFieldValueViewModel[] = [];

    for (const entry of values) {
      const wfFieldId = this.resolveWorkflowFieldIdForApi(workflowId, entry.fieldId ?? '');
      if (!wfFieldId) continue;
      const norm = this.normalizeFieldId(wfFieldId);
      const field = fieldsById.get(norm);

      if (field?.type === 'FORMULA') {
        continue;
      }

      if (field?.type === 'FILE') {
        const v = entry.value;
        if (v instanceof File || (Array.isArray(v) && v.some((x) => x instanceof File))) {
          postSync.push(entry);
          continue;
        }
      }

      const serialized = await this.toPersistedValue('', entry.value, field);
      const str =
        serialized === null
          ? null
          : typeof serialized === 'string'
            ? serialized
            : String(serialized);
      byNorm.set(norm, { workflowFieldId: wfFieldId, value: str });
    }

    return { batch: [...byNorm.values()].filter(item => item.value != null), postSync };
  }

  private normalizeFieldId(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private normalizeExistingFileFieldJson(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const t = value.trim();
    if (!t) return null;
    return parseFileFieldValueToAttachments(t).length > 0 ? t : null;
  }

  /**
   * API de field-values valida FILE com objeto simples (ou array) focado em `fileName/storageKey/assetUrl`.
   * Evita enviar chaves extras que disparem ValidationError.
   */
  private serializeFileAttachmentsForApi(entries: FileFieldAttachment[]): string | null {
    const compact = entries
      .map((entry) => {
        const fileName =
          entry.originalFileName?.trim() || entry.fileName?.trim() || '';
        const storageKey = entry.storageKey?.trim() || '';
        const assetUrl =
          entry.assetUrl?.trim() ||
          entry.url?.trim() ||
          entry.fileUrl?.trim() ||
          entry.path?.trim() ||
          '';
        const out: Record<string, string> = {};
        if (fileName) out['fileName'] = fileName;
        if (storageKey) out['storageKey'] = storageKey;
        if (assetUrl) out['assetUrl'] = assetUrl;
        return out;
      })
      .filter((x) => Object.keys(x).length > 0);
    if (compact.length === 0) return null;
    return compact.length === 1 ? JSON.stringify(compact[0]) : JSON.stringify(compact);
  }

  private async refreshCardValuesCache(
    cardId: string,
    fallbackValues: CustomFieldValueViewModel[]
  ): Promise<void> {
    try {
      const remote = await this.cardFieldValuesApi.getByCard(cardId);
      const normalized: CustomFieldValueViewModel[] = remote.items.map((entry) => ({
        fieldId: entry.workflowFieldId,
        value: entry.value as CustomFieldRawValue,
      }));
      this.customFieldsData.setValuesByCard(cardId, normalized);
    } catch {
      // Sem bloquear fluxo principal: mantém cache local do formulário.
      this.customFieldsData.setValuesByCard(cardId, fallbackValues);
    }
  }
}
